import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchGames, fetchProviders, GAMES_QUERY_KEYS } from '@/features/games/api.js';
import type { GameLike } from '@/components/lobby/GameCard.js';

export interface OriginalsCatalogEntry {
  rtp: number | null;
  /** Card art an operator set in BO → Games (`thumbnailUrl ?? imageUrl`), or null. */
  art: string | null;
  /** Layered tile art from BO → Games (`metadata.layers`), raw; GameCard parses it. */
  layers?: unknown;
  /** The game's Tile from the art library (`metadata.tile`), raw; GameCard parses it. */
  tile?: unknown;
  /** The game's Theme cards (`metadata.themeTiles`), raw; shown in place of `tile` inside their window. */
  themeTiles?: unknown;
}

export type OriginalsCatalog = ReadonlyMap<string, OriginalsCatalogEntry>;

/**
 * The Originals rows of the game catalog, keyed by game code: the RTP svc-game
 * advertises and any art uploaded for them in the backoffice. Fetched by the
 * `originals` provider rather than sliced from a general games page, which on a
 * large catalog never contains them. Empty until loaded; callers keep their
 * built-in art meanwhile.
 */
export function useOriginalsCatalog(): OriginalsCatalog {
  const providers = useQuery({
    queryKey: GAMES_QUERY_KEYS.providers,
    queryFn: fetchProviders,
    staleTime: 60_000,
  });
  const providerId = providers.data?.items.find((p) => p.code === 'originals')?.id;
  const games = useQuery({
    // Suffixed: `list({ providerId: undefined })` hashes the same as the search
    // palette's `list({})` while the provider is still resolving.
    queryKey: [...GAMES_QUERY_KEYS.list({ providerId }), 'originals'],
    queryFn: () => fetchGames({ providerId, pageSize: 50 }),
    enabled: Boolean(providerId),
    staleTime: 60_000,
  });

  return useMemo(
    () =>
      new Map(
        (games.data?.items ?? []).map((g) => [
          g.code,
          { rtp: g.rtp ?? null, art: g.thumbnailUrl ?? g.imageUrl ?? null, layers: g.layers, tile: g.tile, themeTiles: g.themeTiles },
        ]),
      ),
    [games.data],
  );
}

/**
 * The built-in Originals cards with the catalog's RTP and uploaded art filled in
 * by code. A card with no catalog row, or a row with no art, keeps its built-in
 * look.
 */
export function withOriginalsCatalog<T extends GameLike>(cards: readonly T[], catalog: OriginalsCatalog): T[] {
  return cards.map((card) => {
    const row = card.code ? catalog.get(card.code) : undefined;
    if (!row) return card;
    return {
      ...card,
      rtp: row.rtp,
      thumbnailUrl: row.art ?? card.thumbnailUrl ?? null,
      layers: row.layers ?? card.layers,
      tile: row.tile ?? card.tile,
      themeTiles: row.themeTiles ?? card.themeTiles,
    };
  });
}

/**
 * General catalog rows with the art uploaded for an Originals game in BO → Games
 * shown on its card. Originals have no synced cover, so their uploaded image is
 * their only art; other providers' rows keep the sync's cover untouched.
 */
export function withOriginalsRowArt<T extends GameLike & { imageUrl?: string | null }>(rows: readonly T[]): T[] {
  return rows.map((row) =>
    row.providerCode === 'originals' && !row.thumbnailUrl && row.imageUrl ? { ...row, thumbnailUrl: row.imageUrl } : row,
  );
}
